import { z } from "zod";

import { LocalizedTextSchema } from "shared/localizedText";
import type { Locale, LocalizedText } from "shared/localizedText";

export {
  TranslationLocaleSchema,
  PRIMARY_LOCALE,
  TRANSLATION_LOCALES,
  LocaleSchema,
  LocalizedTextSchema,
} from "shared/localizedText";
export type { TranslationLocale, Locale, LocalizedText } from "shared/localizedText";

export const FeelPredicateSchema = z.string();

export const ALL_LOCALES: Locale[] = ["en-us", "en-gb", "sv"];

export const ValidationRuleSchema = z.object({
  predicate: FeelPredicateSchema,
  message: LocalizedTextSchema,
});

export const VisibilitySchema = z.union([z.literal("visible"), z.object({ visibleWhen: FeelPredicateSchema })]);

export const RequirementSchema = z.union([
  z.literal("optional"),
  z.literal("required"),
  z.object({ requiredWhen: FeelPredicateSchema }),
]);

export const ResponseOptionSchema = z.object({
  id: z.number(),
  feel_id: z.string(),
  text: LocalizedTextSchema,
  parent_label: LocalizedTextSchema.nullable(),
  semantic_key: z.string().optional(),
});

export const SelectResponseTypeSchema = z.object({
  _type: z.literal("select"),
  variants: z.array(ResponseOptionSchema),
});

export const SelectOrOtherResponseTypeSchema = z.object({
  _type: z.literal("select-or-other"),
  variants: z.array(ResponseOptionSchema),
});

export const MultiSelectResponseTypeSchema = z.object({
  _type: z.literal("multi-select"),
  variants: z.array(ResponseOptionSchema),
});

export const MultiSelectGroupedResponseTypeSchema = z.object({
  _type: z.literal("multi-select-grouped"),
  groups: z.array(
    z.object({
      label: LocalizedTextSchema,
      variants: z.array(ResponseOptionSchema),
    })
  ),
});

export const MultiSelectOrOtherResponseTypeSchema = z.object({
  _type: z.literal("multi-select-or-other"),
  variants: z.array(ResponseOptionSchema),
});

export const CheckboxResponseTypeSchema = z.object({
  _type: z.literal("checkbox"),
});

export const RangeLabelColorSchema = z.enum(["positive", "negative", "neutral"]);
export type RangeLabelColor = z.infer<typeof RangeLabelColorSchema>;

export const NumberRangeEndpointSchema = z.object({
  value: z.number(),
  color: RangeLabelColorSchema.optional(),
  label: LocalizedTextSchema.optional(),
});

export const NumberRangeResponseTypeSchema = z.object({
  _type: z.literal("number-range"),
  min: NumberRangeEndpointSchema,
  max: NumberRangeEndpointSchema,
});

export const IntegerResponseTypeSchema = z.object({
  _type: z.literal("integer"),
  min: z.number(),
  max: z.number(),
});

export const VasResponseTypeSchema = z.object({
  _type: z.literal("vas"),
  min: z.number(),
  max: z.number(),
});

export const DateResponseTypeSchema = z.object({
  _type: z.literal("date"),
});

export const TextResponseTypeSchema = z.object({
  _type: z.literal("text"),
  min_length: z.number().optional(),
  max_length: z.number().optional(),
  multiline: z.boolean(),
});

export const TextAreaResponseTypeSchema = z.object({
  _type: z.literal("text-area"),
});

export const QuestionResponseTypeSchema = z.discriminatedUnion("_type", [
  SelectResponseTypeSchema,
  SelectOrOtherResponseTypeSchema,
  MultiSelectResponseTypeSchema,
  MultiSelectOrOtherResponseTypeSchema,
  MultiSelectGroupedResponseTypeSchema,
  CheckboxResponseTypeSchema,
  NumberRangeResponseTypeSchema,
  IntegerResponseTypeSchema,
  VasResponseTypeSchema,
  DateResponseTypeSchema,
  TextResponseTypeSchema,
  TextAreaResponseTypeSchema,
]);

export const QuestionSchema = z.object({
  id: z.number(),
  feel_id: z.string(),
  question: LocalizedTextSchema.optional(),
  description: LocalizedTextSchema.optional(),
  response_type: QuestionResponseTypeSchema.nullable().optional(),
  semantic_key: z.string().optional(),
  updated_at: z.string(),
  edited_by: z.string().nullable().optional(),
});

export const QuestionRefSchema = z.object({
  question_id: z.number(),
  visibility: VisibilitySchema,
  requirement: RequirementSchema,
});

export type QuestionRef = z.infer<typeof QuestionRefSchema>;

export type FeelPredicate = z.infer<typeof FeelPredicateSchema>;

export const primaryText = (lt: LocalizedText): string => lt["en-us"] ?? "";
export const emptyLocalizedText = (): LocalizedText => ({ "en-us": "" });
export type ValidationRule = z.infer<typeof ValidationRuleSchema>;
export type Visibility = z.infer<typeof VisibilitySchema>;
export type Requirement = z.infer<typeof RequirementSchema>;
export type SelectResponseType = z.infer<typeof SelectResponseTypeSchema>;
export type SelectOrOtherResponseType = z.infer<typeof SelectOrOtherResponseTypeSchema>;
export type ResponseOption = z.infer<typeof ResponseOptionSchema>;
export type MultiSelectResponseType = z.infer<typeof MultiSelectResponseTypeSchema>;
export type MultiSelectOrOtherResponseType = z.infer<typeof MultiSelectOrOtherResponseTypeSchema>;
export type MultiSelectGroupedResponseType = z.infer<typeof MultiSelectGroupedResponseTypeSchema>;
export type CheckboxResponseType = z.infer<typeof CheckboxResponseTypeSchema>;
export type NumberRangeResponseType = z.infer<typeof NumberRangeResponseTypeSchema>;
export type IntegerResponseType = z.infer<typeof IntegerResponseTypeSchema>;
export type VasResponseType = z.infer<typeof VasResponseTypeSchema>;
export type DateResponseType = z.infer<typeof DateResponseTypeSchema>;
export type TextResponseType = z.infer<typeof TextResponseTypeSchema>;
export type TextAreaResponseType = z.infer<typeof TextAreaResponseTypeSchema>;
export type QuestionResponseType = z.infer<typeof QuestionResponseTypeSchema>;
export type Question = z.infer<typeof QuestionSchema>;
